import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { buildSubmissionSchema } from "@colab/shared";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { extractValidationIssues } from "../forms/use-submit-form";
import { FIELD_RENDERERS } from "../forms/field-renderers/field-registry";
import { useJourneyTaskForm } from "./use-journey-task-form";
import { useSubmitJourneyForm } from "./use-submit-journey-form";

// Slide-out sheet hosting the form for an action.kind === 'form_submission'
// journey task. Replaces /components/portal/journeys/JourneyTaskFormDrawer.
//
// The form definition lazy-loads on open (useJourneyTaskForm); fields render
// via the sub-bucket 2 FIELD_RENDERERS registry; submit routes to the
// bucket-12 useSubmitJourneyForm mutation. File fields render their fallback
// state (no journey-side draft/submission context) — file uploads inside
// journey task forms aren't supported in legacy either; surface for a future
// sub-bucket.

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  progressId: string;
  formId: string;
  taskTitle: string;
};

export function JourneyTaskFormDrawer({
  open,
  onOpenChange,
  progressId,
  formId,
  taskTitle,
}: Props) {
  const formQuery = useJourneyTaskForm(open ? formId : null);
  const submit = useSubmitJourneyForm();
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full overflow-y-auto sm:max-w-xl"
      >
        <SheetHeader className="mb-6">
          <SheetTitle>{taskTitle}</SheetTitle>
          <SheetDescription>
            {formQuery.data
              ? `Submit ${formQuery.data.name} to complete this step.`
              : "Loading form…"}
          </SheetDescription>
        </SheetHeader>

        {formQuery.isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-1/2" />
          </div>
        ) : formQuery.isError ? (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
            {formQuery.error?.message ?? "Couldn’t load this form."}
          </div>
        ) : formQuery.data ? (
          <DrawerBody
            progressId={progressId}
            formId={formId}
            definition={formQuery.data.definition}
            submit={submit}
            onClose={() => onOpenChange(false)}
          />
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

function DrawerBody({
  progressId,
  formId,
  definition,
  submit,
  onClose,
}: {
  progressId: string;
  formId: string;
  definition: NonNullable<
    ReturnType<typeof useJourneyTaskForm>["data"]
  >["definition"];
  submit: ReturnType<typeof useSubmitJourneyForm>;
  onClose: () => void;
}) {
  const fields = definition.sections.flatMap((s) => s.fields);
  const schema = buildSubmissionSchema(definition);
  const form = useForm<Record<string, unknown>>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(schema as any),
    defaultValues: defaultValuesForFields(fields),
  });

  useEffect(() => {
    form.reset(defaultValuesForFields(fields));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId]);

  function onSubmit(values: Record<string, unknown>) {
    submit.mutate(
      { progressId, formId, payload: values },
      {
        onSuccess: () => {
          toast.success("Submitted — task marked complete.");
          onClose();
        },
        onError: (err) => {
          const issues = extractValidationIssues(err);
          if (issues) {
            for (const issue of issues) {
              const path = issue.path?.[0];
              if (typeof path === "string") {
                form.setError(path, { message: issue.message });
              }
            }
            toast.error("Some fields need attention before submitting.");
            return;
          }
          toast.error(err.message ?? "Failed to submit form");
        },
      },
    );
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
      {definition.sections.map((section, sIdx) => (
        <div key={section.id ?? sIdx} className="space-y-4">
          {section.heading ? (
            <div className="border-b border-border/70 pb-2">
              <h3 className="font-display text-base font-semibold tracking-tight text-foreground">
                {section.heading}
              </h3>
              {section.description ? (
                <p className="mt-1 text-sm text-muted-foreground">
                  {section.description}
                </p>
              ) : null}
            </div>
          ) : null}
          {section.fields.map((field) => {
            const Renderer = FIELD_RENDERERS[field.kind];
            if (!Renderer) return null;
            return (
              <Renderer
                key={field.id}
                field={field}
                control={form.control}
                disabled={submit.isPending}
                submissionId={null}
              />
            );
          })}
        </div>
      ))}

      <div className="flex items-center justify-end gap-2 pt-2">
        <Button
          type="button"
          variant="ghost"
          onClick={onClose}
          disabled={submit.isPending}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={submit.isPending}>
          {submit.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : null}
          {definition.submitButtonLabel ?? "Submit"}
        </Button>
      </div>
    </form>
  );
}

function defaultValuesForFields(
  fields: ReadonlyArray<{ name: string; kind: string }>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    out[f.name] = f.kind === "checkbox" ? false : "";
  }
  return out;
}
